// The one fetch every API call goes through (J-210, inbox 06 PR E points 3 and 11): the typed client
// (`api/client.ts`) and Better Auth's client (`features/auth/client.ts`).
//
// 429 means "busy", never "signed out". The server's rate limits answer 429 before any handler runs,
// so nothing happened and the request is safe to send again: it waits the server's Retry-After
// (Better Auth sends X-Retry-After), shows "busy" meanwhile, and tries again, at most RETRY_LIMIT
// times. Signing in is the exception: five failed attempts a minute is a limit on guessing, and the
// sign-in form says so instead of waiting.
//
// 401 from the API means the session has ended (idle, the 12-hour cap, a revocation, a sign-out in
// another tab). The page cannot go on, so the registered handler sends the user to
// /login?reason=expired with the page to return to. Better Auth's own paths are left alone: a 401
// from /api/auth/sign-in is a wrong password, not an ended session.
//
// J-215 (inbox 22 item 1, inbox 06 PR E point 3): every read (GET or HEAD) has 15 seconds in all,
// waits for a 429 included. A read the server never answers then fails with `ReadTimeoutError`, so the
// screen shows its error state with Retry instead of "Loading." or a blank page. Writes have no limit:
// an upload or a calculation can take longer, and sending one again is the caller's decision.
// J-232 (the owner's inbox file 33, item 5b): the two page reads sent as a POST, so a searched name
// never goes into a URL (`/gap-pages`, `/patient-pages`), are reads and have the same 15 seconds.
import { busySignal } from "./busy-signal";

/** How many times one request is sent again after 429. */
export const RETRY_LIMIT = 3;
const DEFAULT_WAIT_SECONDS = 5;
const MAX_WAIT_SECONDS = 60;

/** Seconds to wait before sending again: Retry-After or X-Retry-After, 1 to 60, else 5. */
export function retryAfterSeconds(headers: Headers, now: number = Date.now()): number {
  const raw = (headers.get("retry-after") ?? headers.get("x-retry-after"))?.trim();
  if (raw === undefined || raw === "") return DEFAULT_WAIT_SECONDS;
  const seconds = /^\d+$/.test(raw) ? Number(raw) : Math.ceil((Date.parse(raw) - now) / 1000);
  if (!Number.isFinite(seconds)) return DEFAULT_WAIT_SECONDS;
  return Math.min(MAX_WAIT_SECONDS, Math.max(1, seconds));
}

/** The time one read may take, waits for a 429 included. */
export const READ_TIMEOUT_MS = 15_000;

/** A read the server did not answer within READ_TIMEOUT_MS. */
export class ReadTimeoutError extends Error {
  constructor() {
    super("The server did not answer within 15 seconds.");
    this.name = "ReadTimeoutError";
  }
}

/** An API answer that was not the data asked for; `status` is the HTTP status (200 for a bad body). */
export class ApiReadError extends Error {
  readonly status: number;

  constructor(status: number, code: string | undefined) {
    super(code ?? `http_${status}`);
    this.name = "ApiReadError";
    this.status = status;
  }
}

const pathOf = (url: string): string => new URL(url, "http://local").pathname;

/** The POST paths that only read: one page of a list, its search in the body (J-201, J-202). */
const POST_READ_PATH =
  /^\/api\/v1\/(gap-pages|performance-groups\/\d+\/measures\/\d+\/patient-pages)$/;

/** GET, HEAD and the page reads sent as a POST have the read limit; a write does not. */
export function isRead(method: string, url: string): boolean {
  if (method === "GET" || method === "HEAD") return true;
  return method === "POST" && POST_READ_PATH.test(pathOf(url));
}

/** A signal that aborts with `ReadTimeoutError` after `ms`. */
function readDeadline(ms: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => controller.abort(new ReadTimeoutError()), ms);
  return controller.signal;
}

/**
 * TanStack Query's retry rule for every read: once, and never after a timeout (the read already waited
 * 15 s) or a 4xx answer (asking again gets the same answer; a 429 was already waited out here).
 */
export function retriesRead(failures: number, error: unknown): boolean {
  if (failures >= 1 || error instanceof ReadTimeoutError) return false;
  return !(error instanceof ApiReadError && error.status >= 400 && error.status < 500);
}

/**
 * A 429 here is waited out and sent again; sign-in, two-factor and the password reset requests are
 * not: each is a counted attempt, and the page says to wait instead (J-246: "Forgot password?"
 * allows three a five-minute window, which no page should sit through).
 */
const NO_RETRY_PREFIXES = [
  "/api/auth/sign-in",
  "/api/auth/two-factor",
  "/api/auth/request-password-reset",
  "/api/auth/reset-password",
] as const;

export function retriesOn429(url: string): boolean {
  const path = pathOf(url);
  return !NO_RETRY_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** A 401 here means the session has ended. */
export function endsSession(url: string, status: number): boolean {
  return status === 401 && !pathOf(url).startsWith("/api/auth/");
}

/**
 * J-244: the API's answer while the account must change its password. The page cannot go on, so the
 * registered handler sends the user to the change-password page.
 */
export async function requiresPasswordChange(url: string, response: Response): Promise<boolean> {
  if (response.status !== 403 || !pathOf(url).startsWith("/api/")) return false;
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => undefined);
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { error?: unknown }).error === "password_change_required"
  );
}

let passwordChange: () => void = () => undefined;

/** The router registers what `password_change_required` does (`app/router.tsx`). */
export function setPasswordChangeHandler(handler: () => void): void {
  passwordChange = handler;
}

let unauthorized: () => void = () => undefined;

/** The router registers what a 401 does (`app/router.tsx`); shared code cannot import the router. */
export function setUnauthorizedHandler(handler: () => void): void {
  unauthorized = handler;
}

let endingOnPurpose = 0;

/**
 * J-353: runs `work` (Log out, Lock session) while the session ends on purpose. A read still in flight comes
 * back 401 meanwhile; that 401 is this sign-out, not an expired session, so it reports nothing and the caller's
 * own navigation stands (vbltc-ci run 373: Lock session landed on `reason=expired`, not `locked`).
 */
export async function endingSession<T>(work: () => Promise<T>): Promise<T> {
  endingOnPurpose += 1;
  try {
    return await work();
  } finally {
    endingOnPurpose -= 1;
  }
}

/** An ended session, unless the user is ending it (`endingSession`). */
function sessionEnded(): void {
  if (endingOnPurpose === 0) unauthorized();
}

/** For a caller outside this fetch (a raw `fetch` to Better Auth's admin paths) that saw a 401. */
export function reportUnauthorized(): void {
  sessionEnded();
}

export interface ResilientFetchDeps {
  fetch: (request: Request) => Promise<Response>;
  /** Waits `ms`; rejects with the signal's reason once it aborts. */
  sleep: (ms: number, signal: AbortSignal) => Promise<void>;
  /** The signal that ends a read after `ms` (`readDeadline`). */
  deadline: (ms: number) => AbortSignal;
  /** Starts one request's wait; the returned function ends it. */
  busy: { start: (seconds: number) => () => void };
  onUnauthorized: () => void;
  /** J-244: a 403 `password_change_required`. */
  onPasswordChangeRequired: () => void;
}

export function createResilientFetch(deps: ResilientFetchDeps) {
  return async function resilientFetch(
    input: string | URL | Request,
    init?: RequestInit,
  ): Promise<Response> {
    const asked = new Request(input, init);
    const request = isRead(asked.method, asked.url)
      ? new Request(asked, {
          signal: AbortSignal.any([asked.signal, deps.deadline(READ_TIMEOUT_MS)]),
        })
      : asked;
    let response = await deps.fetch(request.clone());
    for (let attempt = 1; attempt <= RETRY_LIMIT; attempt += 1) {
      if (response.status !== 429 || !retriesOn429(request.url)) break;
      const wait = retryAfterSeconds(response.headers);
      const endWait = deps.busy.start(wait);
      await deps.sleep(wait * 1000, request.signal).finally(endWait);
      response = await deps.fetch(request.clone());
    }
    if (endsSession(request.url, response.status)) deps.onUnauthorized();
    if (await requiresPasswordChange(request.url, response)) deps.onPasswordChangeRequired();
    return response;
  };
}

/** The browser's instance. */
export const resilientFetch = createResilientFetch({
  fetch: (request) => globalThis.fetch(request),
  sleep: (ms, signal) =>
    new Promise((resolve, reject) => {
      if (signal.aborted) return reject(signal.reason as Error);
      const timer = setTimeout(resolve, ms);
      signal.addEventListener(
        "abort",
        () => {
          clearTimeout(timer);
          reject(signal.reason as Error);
        },
        { once: true },
      );
    }),
  deadline: readDeadline,
  busy: busySignal,
  onUnauthorized: sessionEnded,
  onPasswordChangeRequired: () => passwordChange(),
});
